import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../utils/cn';

const CANVAS = { width: 1920, height: 1080 } as const;

interface SlideFrameProps {
  children: ReactNode;
  index?: number;
  label?: string;
  className?: string;
}

/**
 * Fixed 1920×1080 logical canvas scaled to fit the parent container width.
 * Outer fills 100% of its parent's content box (via aspect-ratio for height); a
 * ResizeObserver reads the rendered width to drive the inner canvas's CSS
 * transform-scale. Canvas uses a single-cell grid to stretch the agent's
 * `flex flex-col` section to fill 1920×1080.
 */
export function SlideFrame({ children, index, label, className }: SlideFrameProps) {
  const outerRef = useRef<HTMLDivElement>(null);
  const [renderedWidth, setRenderedWidth] = useState(0);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const compute = () => setRenderedWidth(el.clientWidth);
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scale = renderedWidth > 0 ? renderedWidth / CANVAS.width : 1;

  return (
    <div
      ref={outerRef}
      data-slide-frame
      data-slide-frame-index={index}
      data-screen-label={label ?? (index !== undefined ? `${String(index + 1).padStart(2, '0')} Slide` : undefined)}
      className={cn('relative w-full overflow-hidden', className)}
      style={{ aspectRatio: `${CANVAS.width} / ${CANVAS.height}` }}
    >
      <div
        data-slide-frame-canvas
        style={{
          width: CANVAS.width,
          height: CANVAS.height,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr',
        }}
        className="absolute left-0 top-0"
      >
        {children}
      </div>
    </div>
  );
}
